import { Alert, Box, Grid, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import z from 'zod';

import {
  ActionBar,
  BrowserStorageVariant,
  Loader,
  PanelAndContentPageBody,
  StandardPageLayout,
  Text,
  api,
  getQueryProps,
  isDefined,
  isTruthy,
  useBrowserStorage,
} from 'common';

import { FlowTemplateCardWithLink } from './CardWithLink';
import { FlowTemplateFeaturedCard } from './FeaturedCard';
import { FlowTemplateFilterGroupState } from './Filters/FilterGroup';
import { FlowTemplatePanel } from './Panel';
import {
  FlowTemplateVisibility,
  type UniqueTemplateTagNameByCategory,
  compareTemplatesByFeaturedTimestamp,
  doesTemplateMatchSelectedTags,
} from './utils';
import { usePageTitle } from '../../../../PageTitle';
import { useAgentApi } from '../../common/useAgentApi';

export function NewFlowTemplatePickerPage() {
  usePageTitle('Flow Library');
  const { agentId } = useParams<{
    /**
     * This menu is on two routes, one with agentId and one without
     */
    agentId?: string;
  }>();
  const { storedValues, updateStoredValue, setStoredValues } = useBrowserStorage({
    storageKeyName: `FlowLibrary:Filters:${isDefined(agentId) ? agentId : 'Global'}`,
    variant: BrowserStorageVariant.SessionStorage,
    schema: z.object({
      solutionAreaTagNames: z.array(z.string()).optional(),
      useCaseTagNames: z.array(z.string()).optional(),
      flowTypeTagNames: z.array(z.string()).optional(),
      supportedEhrTagNames: z.array(z.string()).optional(),
      visibility: z.array(z.enum(FlowTemplateVisibility)).optional(),
    }),
    initialValue: {},
  });

  const { agentResponse } = useAgentApi({ agentId });

  const [searchValue, setSearchValue] = useState('');

  const { data: baseFlowTemplates = [], isPending } = useQuery({
    ...getQueryProps(api.client.FlowDefinitionTemplate, 'findAll', {
      orderBy: 'name',
      orderDirection: 'asc' as const,
      visibility: isTruthy(storedValues.visibility?.length)
        ? storedValues.visibility
        : [FlowTemplateVisibility.Global, FlowTemplateVisibility.Organization],
    }),
    select: (templates) => {
      if (isEmpty(searchValue)) {
        return templates;
      }
      const normalizedSearchValue = searchValue.trim().toLowerCase();
      return templates.filter(
        (template) =>
          template.name.toLowerCase().includes(normalizedSearchValue) ||
          template.content.toLowerCase().includes(normalizedSearchValue)
      );
    },
  });

  const solutionAreaTagNames = storedValues.solutionAreaTagNames ?? [];
  const [solutionAreaSectionState, setSolutionAreaSectionState] = useState(() =>
    isTruthy(solutionAreaTagNames?.length)
      ? FlowTemplateFilterGroupState.Expanded
      : FlowTemplateFilterGroupState.Collapsed
  );
  const useCaseTagNames = storedValues.useCaseTagNames ?? [];
  const [useCaseSectionState, setUseCaseSectionState] = useState(() =>
    isTruthy(useCaseTagNames?.length)
      ? FlowTemplateFilterGroupState.Expanded
      : FlowTemplateFilterGroupState.Collapsed
  );
  const flowTypeTagNames = storedValues.flowTypeTagNames ?? [];
  const [flowTypeSectionState, setFlowTypeSectionState] = useState(() =>
    isTruthy(flowTypeTagNames?.length)
      ? FlowTemplateFilterGroupState.Expanded
      : FlowTemplateFilterGroupState.Collapsed
  );
  const supportedEhrTagNames = storedValues.supportedEhrTagNames ?? [];
  const [supportedEhrSectionState, setSupportedEhrSectionState] = useState(() =>
    isTruthy(supportedEhrTagNames?.length)
      ? FlowTemplateFilterGroupState.Expanded
      : FlowTemplateFilterGroupState.Collapsed
  );
  const [visibilitySectionState, setVisibilitySectionState] = useState(() =>
    isTruthy(storedValues.visibility?.length)
      ? FlowTemplateFilterGroupState.Expanded
      : FlowTemplateFilterGroupState.Collapsed
  );

  if (isPending) {
    return (
      <Stack flex={1} justifyContent="center">
        <Loader label="Loading library..." />
      </Stack>
    );
  }

  const selectedTagsByCategory: UniqueTemplateTagNameByCategory = {
    [api.FlowDefinitionTemplate.TagCategory.SolutionArea]: new Set(solutionAreaTagNames),
    [api.FlowDefinitionTemplate.TagCategory.UseCase]: new Set(useCaseTagNames),
    [api.FlowDefinitionTemplate.TagCategory.FlowType]: new Set(flowTypeTagNames),
    [api.FlowDefinitionTemplate.TagCategory.SupportedEhr]: new Set(supportedEhrTagNames),
  };
  const visibleTemplates = baseFlowTemplates.filter((template) =>
    doesTemplateMatchSelectedTags({
      template,
      selectedTagsByCategory,
    })
  );

  const isFilteringTemplates =
    isTruthy(searchValue) ||
    Object.entries(storedValues).some(
      ([key, value]) => key !== 'visibility' && isTruthy(value?.length)
    );

  // Only show featured template if there are no searches
  const featuredTemplate = !isFilteringTemplates
    ? visibleTemplates
        .filter((template) => isTruthy(template.metadata?.isFeaturedTemplate))
        .sort(compareTemplatesByFeaturedTimestamp)[0]
    : undefined;
  return (
    <StandardPageLayout
      actionBar={
        <ActionBar
          breadcrumbProps={{
            activeRoute: {
              label: 'Flow Library',
            },
            links: isDefined(agentId)
              ? [
                  { label: 'Agents', to: '/agents' },
                  {
                    label: agentResponse?.data?.name ?? 'Agent Details',
                    to: `..`,
                    isPending: agentResponse.isPending,
                  },
                ]
              : undefined,
          }}
        />
      }
    >
      <PanelAndContentPageBody
        paddingVariants={{
          panel: 'None',
        }}
        panelContent={
          <FlowTemplatePanel
            storedValues={storedValues}
            updateStoredValue={updateStoredValue}
            setStoredValues={setStoredValues}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            selectedTagsByCategory={selectedTagsByCategory}
            baseFlowTemplates={baseFlowTemplates}
            filterGroupStates={{
              solutionArea: {
                state: solutionAreaSectionState,
                onStateChange: setSolutionAreaSectionState,
              },
              useCase: {
                state: useCaseSectionState,
                onStateChange: setUseCaseSectionState,
              },
              flowType: {
                state: flowTypeSectionState,
                onStateChange: setFlowTypeSectionState,
              },
              supportedEhr: {
                state: supportedEhrSectionState,
                onStateChange: setSupportedEhrSectionState,
              },
              visibility: {
                state: visibilitySectionState,
                onStateChange: setVisibilitySectionState,
              },
            }}
          />
        }
      >
        <Stack gap={4} paddingTop={2}>
          {isEmpty(visibleTemplates) && <Alert severity="info">No templates found</Alert>}

          {!isEmpty(visibleTemplates) && (
            <>
              {isDefined(featuredTemplate) && (
                <Box>
                  <FlowTemplateFeaturedCard flowTemplate={featuredTemplate} />
                </Box>
              )}
              {isFilteringTemplates && (
                <Box>
                  <Text variant="heading4" bold>
                    Results ({visibleTemplates.length})
                  </Text>
                </Box>
              )}
              <Grid container spacing={2}>
                {visibleTemplates.map(
                  (flowTemplate: api.FlowDefinitionTemplate.FlowDefinitionTemplate) => {
                    return (
                      <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }} key={flowTemplate.id}>
                        <FlowTemplateCardWithLink flowTemplate={flowTemplate} />
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </>
          )}
        </Stack>
      </PanelAndContentPageBody>
    </StandardPageLayout>
  );
}
